import { supabase } from '../../providers/supabase/client'

interface Hotel {
    name: string
    stars: number
    description?: string
    main_image_url?: string | null
    gallery_urls?: string[]
    video_urls?: string[]
}

// ─── CRUD ───────────────────────────────────────────────────

export const fetchHotels = async () => {
    return await supabase
        .from('hotels')
        .select(`
            id, created_at, name, stars, description, main_image_url, gallery_urls, video_urls,
            reservations_hotels(count),
            places_hotels(
                places:places_id(id, name)
            )
        `)
}

export const createHotel = async (hotel: Hotel) => {
    return await supabase
        .from('hotels')
        .insert([hotel])
        .select()
}

export const addManyHotels = async (hotels: Hotel[]) => {
    return await supabase
        .from('hotels')
        .insert(hotels)
        .select()
}

export const updateHotel = async (id: number, hotel: Partial<Hotel>) => {
    return await supabase
        .from('hotels')
        .update(hotel)
        .eq('id', id)
        .select()
}

export const deleteHotel = async (id: number) => {
    return await supabase
        .from('hotels')
        .delete()
        .eq('id', id)
}

// ─── STORAGE ────────────────────────────────────────────────

const BUCKET = 'hotel-images'

/**
 * Upload a hotel image to a deterministic slot.
 * Slot examples: 'main', 'gallery_0', 'gallery_1', etc.
 * Uses upsert so re-uploads overwrite the same object.
 */
export const uploadHotelImage = async (
    hotelId: number,
    file: File,
    slot: string,
): Promise<string | null> => {
    const path = `${hotelId}/${slot}`

    const { error } = await supabase.storage
        .from(BUCKET)
        .upload(path, file, {
            upsert: true,
            contentType: file.type,
            cacheControl: '0',
        })

    if (error) {
        console.error(`Error uploading hotel image [${slot}]:`, error)
        return null
    }

    const { data: urlData } = supabase.storage
        .from(BUCKET)
        .getPublicUrl(path)

    return urlData?.publicUrl ? `${urlData.publicUrl}?t=${Date.now()}` : null
}

/**
 * Delete a specific hotel image from storage by slot.
 */
export const deleteHotelImage = async (
    hotelId: number,
    slot: string,
): Promise<boolean> => {
    const path = `${hotelId}/${slot}`

    const { error } = await supabase.storage
        .from(BUCKET)
        .remove([path])

    if (error) {
        console.error(`Error deleting hotel image [${slot}]:`, error)
        return false
    }
    return true
}

const VIDEO_BUCKET = 'hotel-videos'

/**
 * Upload a hotel video to a deterministic slot.
 * Slot examples: 'video_0', 'video_1', etc.
 * Uses upsert so re-uploads overwrite the same object.
 */
export const uploadHotelVideo = async (
    hotelId: number,
    file: File,
    slot: string,
): Promise<string | null> => {
    const path = `${hotelId}/${slot}`

    const { error } = await supabase.storage
        .from(VIDEO_BUCKET)
        .upload(path, file, {
            upsert: true,
            contentType: file.type,
            cacheControl: '0',
        })

    if (error) {
        console.error(`Error uploading hotel video [${slot}]:`, error)
        return null
    }

    const { data: urlData } = supabase.storage
        .from(VIDEO_BUCKET)
        .getPublicUrl(path)

    return urlData?.publicUrl ? `${urlData.publicUrl}?t=${Date.now()}` : null
}

/**
 * Delete a specific hotel video from storage by slot.
 */
export const deleteHotelVideo = async (
    hotelId: number,
    slot: string,
): Promise<boolean> => {
    const path = `${hotelId}/${slot}`

    const { error } = await supabase.storage
        .from(VIDEO_BUCKET)
        .remove([path])

    if (error) {
        console.error(`Error deleting hotel video [${slot}]:`, error)
        return false
    }
    return true
}

